/**
 * Bundle Builder v2 — Bearbottom Clothing
 * Vanilla JS — no dependencies
 * Compatible with Impulse 8.1.0 by Archetype Themes
 *
 * v2 Changes:
 * - Hidden variants filtering from Customizer settings
 * - Swatch colors use Shopify swatch API data (image + CSS color fallback)
 * - Tier selector shows pack name + discount subtext from metafield
 * - Best Value badge (auto-largest tier or metafield override)
 * - Mobile header price/tier synced with desktop
 *
 * v3 Changes:
 * - Inventory status block driven by JS instead of server-side snippet
 * - inventory_quantity included per variant in JSON data
 * - Display is pack-size aware: shows bundles available, not raw item count
 * - Duplicate color selections checked against actual stock before add to cart
 */

(function () {
  'use strict';

  function initBundleBuilder(container) {
    var sectionId = container.dataset.sectionId;
    var dataEl = document.getElementById('BundleData-' + sectionId);
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[BundleBuilder] Failed to parse variant data:', e);
      return;
    }

    var config = data.config;
    var variants = data.variants;
    var swatches = data.swatches;

    /* ============================
       PARSE HIDDEN VARIANTS
       ============================ */
    function parseHiddenList(str) {
      if (!str || typeof str !== 'string') return {};
      var map = {};
      var parts = str.split(',');
      for (var i = 0; i < parts.length; i++) {
        var val = parts[i].replace(/^\s+|\s+$/g, ''); /* trim */
        if (val) map[val.toLowerCase()] = true;
      }
      return map;
    }

    var hiddenSizes = parseHiddenList(config.hiddenSizes);
    var hiddenInseams = parseHiddenList(config.hiddenInseams);
    var hiddenColors = parseHiddenList(config.hiddenColors);

    /* ============================
       EXTRACT UNIQUE OPTIONS FROM VARIANTS
       ============================ */
    var options = { waists: [], lengths: [], colors: [] };
    if (config.showFit) options.fits = [];

    var seenW = {}, seenL = {}, seenC = {}, seenF = {};
    var variantKeys = Object.keys(variants);
    for (var vi = 0; vi < variantKeys.length; vi++) {
      var v = variants[variantKeys[vi]];
      if (v.waist && !seenW[v.waist] && !hiddenSizes[v.waist.toLowerCase()]) {
        seenW[v.waist] = true; options.waists.push(v.waist);
      }
      if (v.length && !seenL[v.length] && !hiddenInseams[v.length.toLowerCase()]) {
        seenL[v.length] = true; options.lengths.push(v.length);
      }
      if (v.color && !seenC[v.color] && !hiddenColors[v.color.toLowerCase()]) {
        seenC[v.color] = true; options.colors.push(v.color);
      }
      if (config.showFit && v.fit && !seenF[v.fit]) {
        seenF[v.fit] = true; options.fits.push(v.fit);
      }
    }

    /* True when the source product has an inseam/length option.
       Products like tops or windbreakers may only have size + color.
       All inseam requirements are gated on this flag throughout. */
    var hasInseam = options.lengths.length > 0;

    console.log('[BundleBuilder] Options extracted (after hiding):', {
      waists: options.waists,
      lengths: options.lengths,
      colors: options.colors,
      fits: options.fits || [],
      hasInseam: hasInseam,
      hidden: { sizes: Object.keys(hiddenSizes), inseams: Object.keys(hiddenInseams), colors: Object.keys(hiddenColors) }
    });

    /* ============================
       STATE
       ============================ */
    var state = {
      fit: null,
      waist: null,
      length: null,
      colors: new Array(config.maxBundleSize).fill(null),
      activeColorSlot: 0,
      showGallery: config.showShellGallery !== false && !!(config.shellImages && config.shellImages.length > 0),
      currentSlide: 0,
      currentTierIndex: 0
    };

    if (config.tiers && config.tiers.length > 0) {
      for (var ti = 0; ti < config.tiers.length; ti++) {
        if (config.tiers[ti].pack_size === config.bundleSize) {
          state.currentTierIndex = ti;
          break;
        }
      }
    }

    /* Tracks the first unselected option so the add-to-cart button
       can display a targeted prompt and scroll-to-help on click.
       Set by validate() on every selection change. */
    var _firstMissing = null;

    /* ============================
       DOM REFERENCES
       ============================ */
    var els = {
      fitContainer: container.querySelector('.bundle-builder__options--fit'),
      waistContainer: container.querySelector('.bundle-builder__options--waist'),
      lengthContainer: container.querySelector('.bundle-builder__options--length'),
      waistDisplay: container.querySelector('[data-waist-display]'),
      lengthDisplay: container.querySelector('[data-length-display]'),
      colorSlots: container.querySelectorAll('.bundle-builder__color-slot'),
      colorSwatchContainers: container.querySelectorAll('.bundle-builder__color-swatches'),
      fitDisplay: container.querySelector('[data-fit-display]'),
      addBtn: container.querySelector('[data-add-to-cart]'),
      addBtnText: container.querySelector('.bundle-builder__add-btn-text'),
      errorEl: container.querySelector('.bundle-builder__error'),
      srAnnounce: container.querySelector('.bundle-builder__sr-announce'),
      gallery: container.querySelector('[data-gallery]'),
      galleryMainImg: container.querySelector('[data-gallery-main-img]'),
      galleryMain: container.querySelector('[data-gallery-main]'),
      galleryThumbs: container.querySelectorAll('[data-gallery-thumb]'),
      galleryThumbsContainer: container.querySelector('[data-gallery-thumbs]'),
      galleryDots: container.querySelectorAll('[data-gallery-dot]'),
      thumbArrowPrev: container.querySelector('[data-thumb-arrow-prev]'),
      thumbArrowNext: container.querySelector('[data-thumb-arrow-next]'),
      builderGrid: container.querySelector('[data-builder-grid]'),
      builderSlider: container.querySelector('[data-builder-slider]'),
      pairCards: container.querySelectorAll('[data-pair-card]'),
      pairImages: container.querySelectorAll('[data-pair-image]'),
      sliderTrack: container.querySelector('[data-slider-track]'),
      slides: container.querySelectorAll('[data-slide]'),
      slideImages: container.querySelectorAll('[data-slide-image]'),
      sliderPrev: container.querySelector('[data-slider-prev]'),
      sliderNext: container.querySelector('[data-slider-next]'),
      sliderDots: container.querySelectorAll('[data-dot]'),
      /* Desktop tier & price */
      tierSelector: container.querySelector('[data-tier-selector]'),
      originalPriceEl: container.querySelector('[data-original-price]'),
      bundlePriceEl: container.querySelector('[data-bundle-price]'),
      /* Mobile price (in sh-mobile header — no separate mobile tier selector) */
      originalPriceMobile: document.querySelector('[data-original-price-mobile]'),
      bundlePriceMobile: document.querySelector('[data-bundle-price-mobile]'),
      /* Single color mode image container */
      singleColorImage: container.querySelector('[data-single-color-image]'),
      /* Apply to all button — injected dynamically, referenced after creation */
      applyToAllBtn: null,
      /* Inventory status elements — rendered by liquid, driven by JS */
      inventoryStatus: container.querySelector('[data-inventory-status]'),
      inventoryDot: container.querySelector('[data-inventory-dot]'),
      inventoryText: container.querySelector('[data-inventory-text]')
    };

    /* ============================
       SCREEN READER ANNOUNCE
       ============================ */
    function announce(message) {
      if (!els.srAnnounce) return;
      els.srAnnounce.textContent = '';
      requestAnimationFrame(function () {
        els.srAnnounce.textContent = message;
      });
    }

    /* ============================
       TIER & PRICING
       ============================ */
    function getCurrentTier() {
      if (!config.tiers || config.tiers.length === 0) return null;
      return config.tiers[state.currentTierIndex] || config.tiers[0];
    }

    function getActiveBundleSize() {
      var tier = getCurrentTier();
      return tier ? tier.pack_size : config.bundleSize;
    }

    function formatMoney(cents) {
      var dollars = (cents / 100).toFixed(2);
      if (dollars.indexOf('.00') === dollars.length - 3) {
        dollars = dollars.slice(0, -3);
      }
      return '$' + dollars;
    }

    function calculatePricing(tier) {
      if (!tier) return null;
      var itemPrice = config.itemPriceCents;
      var packSize = tier.pack_size;
      var originalTotal = itemPrice * packSize;
      var bundleTotal;

      if (tier.type === 'percentage') {
        bundleTotal = Math.round(originalTotal * (1 - tier.value / 100));
      } else if (tier.type === 'fixed_total') {
        bundleTotal = Math.round(tier.value * 100);
      } else if (tier.type === 'fixed_per_item') {
        bundleTotal = Math.round(tier.value * 100) * packSize;
      } else {
        return null;
      }

      return {
        originalTotal: originalTotal,
        bundleTotal: bundleTotal,
        savings: originalTotal - bundleTotal,
        savingsPercent: Math.round((1 - bundleTotal / originalTotal) * 100)
      };
    }

    function getSavingsLabel(tier) {
      var pricing = calculatePricing(tier);
      if (!pricing || pricing.savings <= 0) return '';

      if (config.tierSubtextPerItem) {
        /* Per-item price mode */
        var perItemCents;
        if (tier.type === 'percentage') {
          perItemCents = Math.round(config.itemPriceCents * (1 - tier.value / 100));
        } else if (tier.type === 'fixed_per_item') {
          perItemCents = Math.round(tier.value * 100);
        } else if (tier.type === 'fixed_total') {
          perItemCents = Math.round((tier.value * 100) / tier.pack_size);
        } else {
          return '';
        }
        return formatMoney(perItemCents) + '/ea';
      }

      /* Default: savings mode */
      if (tier.type === 'percentage') {
        return 'Save ' + tier.value + '%';
      } else if (tier.type === 'fixed_total') {
        return 'Save ' + formatMoney(pricing.savings);
      } else if (tier.type === 'fixed_per_item') {
        return formatMoney(Math.round(tier.value * 100)) + '/ea';
      }
      return '';
    }

    function updatePriceDisplay() {
      var tier = getCurrentTier();
      var pricing = calculatePricing(tier);

      var targets = [
        { price: els.originalPriceEl, bundle: els.bundlePriceEl },
        { price: els.originalPriceMobile, bundle: els.bundlePriceMobile }
      ];

      for (var t = 0; t < targets.length; t++) {
        var priceEl = targets[t].price;
        var bundleEl = targets[t].bundle;
        if (!pricing) {
          if (priceEl) priceEl.style.display = 'none';
          if (bundleEl) bundleEl.textContent = '';
          continue;
        }
        if (priceEl) {
          priceEl.textContent = formatMoney(pricing.originalTotal);
          priceEl.style.display = pricing.savings > 0 ? '' : 'none';
        }
        if (bundleEl) {
          bundleEl.textContent = formatMoney(pricing.bundleTotal);
        }
      }
    }

    /* ============================
       BEST VALUE BADGE LOGIC
       ============================ */
    function getBadgeTierIndex() {
      /* Legacy: returns the single auto-fallback index when no explicit badges exist.
         Used only to determine the auto-largest tier for fallback mode. */
      if (!config.showBestValue || !config.tiers || config.tiers.length <= 1) return -1;
      /* If any tier has an explicit badge, disable auto mode entirely */
      for (var i = 0; i < config.tiers.length; i++) {
        if (config.tiers[i].badge) return -1;
      }
      /* Auto mode: apply to largest pack size only */
      var largestIdx = 0;
      for (var i = 1; i < config.tiers.length; i++) {
        if (config.tiers[i].pack_size > config.tiers[largestIdx].pack_size) {
          largestIdx = i;
        }
      }
      return largestIdx;
    }

    function renderTierSelector() {
      if (!config.tiers || config.tiers.length <= 1) return;

      var badgeTierIdx = getBadgeTierIndex();
      var html = buildTierHTML(badgeTierIdx);

      /* Single tier selector — visible on both desktop and mobile */
      if (!els.tierSelector) return;
      els.tierSelector.style.display = '';
      els.tierSelector.innerHTML = html;

      var tierBtns = els.tierSelector.querySelectorAll('[data-tier-btn]');
      for (var b = 0; b < tierBtns.length; b++) {
        (function(btn) {
          btn.addEventListener('click', function () {
            var idx = parseInt(btn.dataset.tierBtn, 10);
            switchTier(idx);
          });
        })(tierBtns[b]);
      }

      var tierGroup = els.tierSelector.querySelector('[role="group"]');
      if (tierGroup) setupArrowKeyNav(tierGroup);
    }

    function buildTierHTML(badgeTierIdx) {
      var html = '<div class="variant-wrapper" data-type="button">';
      html += '<label class="variant__label">Pack Size</label>';
      html += '<fieldset class="variant-input-wrap" name="Pack Size">';
      html += '<legend class="hide">Pack Size</legend>';
      html += '<div class="bundle-builder__options bundle-builder__options--tier" role="group" aria-label="Pack size selection">';

      for (var i = 0; i < config.tiers.length; i++) {
        var tier = config.tiers[i];
        var isActive = i === state.currentTierIndex;
        var savingsLabel = getSavingsLabel(tier);
        var hasBadge = config.showBestValue && (
          tier.badge ||                          /* explicit badge on this tier */
          i === badgeTierIdx                     /* auto mode: largest tier, only when no explicit badges */
        );
        var badgeLabel = (tier.badge) || config.bestValueText || 'BEST VALUE';

        html += '<button type="button" class="bundle-builder__tier-btn"';
        html += ' data-tier-btn="' + i + '"';
        html += ' aria-pressed="' + isActive + '"';
        html += ' tabindex="' + (isActive ? '0' : '-1') + '"';
        if (isActive) html += ' style="box-shadow: 0 0 0 2px var(--colorTextBody);"';
        html += '>';

        /* Best Value badge */
        if (hasBadge) {
          html += '<span class="bundle-builder__best-value-badge" style="background-color:' + escapeAttr(config.bestValueBg || '#1a1a1a') + ';color:' + escapeAttr(config.bestValueColor || '#fff') + ';">';
          html += escapeHTML(badgeLabel);
          html += '</span>';
        }

        /* Pack name */
        html += '<span class="bundle-builder__tier-name">' + tier.pack_size + ' Pack</span>';

        /* Discount subtext */
        if (savingsLabel) {
          html += '<span class="bundle-builder__tier-discount">' + savingsLabel + '</span>';
        }

        html += '</button>';
      }

      html += '</div></fieldset></div>';
      return html;
    }

    function syncTierButtons() {
      if (!els.tierSelector) return;
      var allBtns = els.tierSelector.querySelectorAll('[data-tier-btn]');
      for (var b = 0; b < allBtns.length; b++) {
        var btn = allBtns[b];
        var idx = parseInt(btn.dataset.tierBtn, 10);
        var isActive = idx === state.currentTierIndex;
        btn.setAttribute('aria-pressed', isActive.toString());
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
        btn.style.boxShadow = isActive ? '0 0 0 2px var(--colorTextBody)' : '';
      }
    }

    function switchTier(tierIndex) {
      if (tierIndex === state.currentTierIndex) return;
      if (!config.tiers[tierIndex]) return;

      var oldSize = getActiveBundleSize();
      state.currentTierIndex = tierIndex;
      var newSize = getActiveBundleSize();
      config.bundleSize = newSize;

      if (newSize > oldSize) {
        for (var i = oldSize; i < newSize; i++) {
          state.colors[i] = null;
        }
      } else if (newSize < oldSize) {
        for (var i = newSize; i < oldSize; i++) {
          state.colors[i] = null;
          var colorDisplay = container.querySelector('[data-color-display="' + i + '"]');
          if (colorDisplay) colorDisplay.textContent = '';
          /* Use renderImage so the persistent <img> is updated in place —
             avoids innerHTML collapse that causes iOS layout shift */
          if (els.pairImages[i]) renderImage(els.pairImages[i], i, els.pairCards[i] || null);
          if (els.slideImages[i]) renderImage(els.slideImages[i], i, null);
        }
      }

      els.colorSlots.forEach(function (slot, i) { slot.style.display = i < newSize ? '' : 'none'; });
      els.pairCards.forEach(function (card, i) { card.style.display = i < newSize ? '' : 'none'; });
      els.slides.forEach(function (slide, i) { slide.style.display = i < newSize ? '' : 'none'; });
      els.sliderDots.forEach(function (dot, i) { dot.style.display = i < newSize ? '' : 'none'; });

      syncTierButtons();

      if (state.currentSlide >= newSize) goToSlide(newSize - 1);

      var firstEmpty = -1;
      for (var i = 0; i < newSize; i++) {
        if (state.colors[i] === null) { firstEmpty = i; break; }
      }
      if (firstEmpty >= 0) {
        setActiveColorSlot(firstEmpty);
        goToSlide(firstEmpty);
      }

      updatePriceDisplay();
      updateColorAvailability();
      updateColorSlotUI();
      updateBuilderImages();
      updateApplyToAllBtn();
      validate();
      announce(newSize + '-Pack selected');
    }

    /* ============================
       RENDER OPTION BUTTONS
       ============================ */
    if (config.showFit && els.fitContainer && options.fits) {
      els.fitContainer.innerHTML = '';
      options.fits.forEach(function (fit) {
        var btn = createOptionButton(fit, fit);
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          state.fit = fit;
          announce('Fit selected: ' + fit);
          updateFitUI();
          updateWaistAvailability();
          updateLengthAvailability();
          updateColorAvailability();
          validate();
        });
        els.fitContainer.appendChild(btn);
      });
      setupArrowKeyNav(els.fitContainer);
    }

    if (els.waistContainer && options.waists) {
      els.waistContainer.innerHTML = '';
      options.waists.forEach(function (waist) {
        var btn = createOptionButton(waist, waist);
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          state.waist = waist;
          announce('Size selected: ' + waist);
          updateWaistUI();
          updateLengthAvailability();
          updateColorAvailability();
          updateImageGrid();
          validate();
        });
        els.waistContainer.appendChild(btn);
      });
      setupArrowKeyNav(els.waistContainer);
    }

    if (els.lengthContainer && options.lengths) {
      els.lengthContainer.innerHTML = '';
      options.lengths.forEach(function (length) {
        var btn = createOptionButton(length, length);
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          state.length = length;
          announce('Inseam selected: ' + length);
          updateLengthUI();
          updateColorAvailability();
          updateImageGrid();
          validate();
        });
        els.lengthContainer.appendChild(btn);
      });
      setupArrowKeyNav(els.lengthContainer);
    }

    if (options.colors) {
      els.colorSwatchContainers.forEach(function (swatchContainer, slotIndex) {
        swatchContainer.innerHTML = '';
        options.colors.forEach(function (color) {
          var swatchData = swatches[color] || {};
          var swatch = createColorSwatch(color, swatchData);
          swatch.addEventListener('click', function () {
            if (swatch.disabled) return;
            selectColor(slotIndex, color);
          });
          swatchContainer.appendChild(swatch);
        });
        setupArrowKeyNav(swatchContainer);
      });
    }

    els.colorSlots.forEach(function (slot, index) {
      slot.addEventListener('click', function (e) {
        if (e.target.closest('.bundle-builder__swatch')) return;
        setActiveColorSlot(index);
      });
    });

    /* ============================
       ARROW KEY NAVIGATION (ADA)
       ============================ */
    function setupArrowKeyNav(containerEl) {
      containerEl.addEventListener('keydown', function (e) {
        var buttons = Array.prototype.slice.call(
          containerEl.querySelectorAll('button:not([disabled])')
        );
        if (!buttons.length) return;

        var currentIndex = buttons.indexOf(document.activeElement);
        var nextIndex = -1;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        } else if (e.key === 'Home') {
          e.preventDefault();
          nextIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          nextIndex = buttons.length - 1;
        }

        if (nextIndex >= 0) buttons[nextIndex].focus();
      });
    }

    /* ============================
       ELEMENT CREATORS
       ============================ */
    function createOptionButton(label, value) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__option-btn';
      btn.textContent = label;
      btn.dataset.value = value;
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('tabindex', '-1');
      return btn;
    }

    function createColorSwatch(colorName, swatchData) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__swatch';
      btn.dataset.color = colorName;
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', colorName);
      btn.setAttribute('tabindex', '-1');

      var inner = document.createElement('span');
      inner.className = 'bundle-builder__swatch-inner';

      /* Priority: swatch image > swatch color > fallback
         Matches variant-button.liquid logic */
      if (swatchData.image) {
        inner.style.backgroundImage = 'url(' + swatchData.image + ')';
        inner.style.backgroundSize = 'cover';
        inner.style.backgroundPosition = 'center';
      } else if (swatchData.color) {
        /* CSS color name from Shopify swatch API or last-word fallback */
        inner.style.backgroundColor = swatchData.color;
      } else {
        /* Last resort: generate from name hash */
        inner.style.backgroundColor = generateFallbackColor(colorName);
      }
      btn.appendChild(inner);

      var tooltip = document.createElement('span');
      tooltip.className = 'bundle-builder__swatch-tooltip';
      tooltip.textContent = colorName;
      tooltip.id = 'tt-' + colorName.toLowerCase().replace(/\s+/g, '-') + '-' + Math.random().toString(36).substring(2, 6);
      btn.setAttribute('aria-describedby', tooltip.id);
      btn.appendChild(tooltip);

      return btn;
    }

    function generateFallbackColor(name) {
      /* Better hash that avoids near-white results */
      var hash = 0;
      for (var i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      /* Use constrained lightness (30-60%) so colors are always visible */
      var hue = Math.abs(hash) % 360;
      var lightness = 30 + (Math.abs(hash >> 8) % 30);
      return 'hsl(' + hue + ', 35%, ' + lightness + '%)';
    }

    /* ============================
       UI UPDATES
       ============================ */
    function updateFitUI() {
      if (!els.fitContainer) return;
      updateRadioGroup(els.fitContainer, state.fit);
      if (els.fitDisplay) els.fitDisplay.textContent = state.fit || '';
    }

    function updateWaistUI() {
      updateRadioGroup(els.waistContainer, state.waist);
      if (els.waistDisplay) els.waistDisplay.textContent = state.waist || '';
    }

    function updateLengthUI() {
      updateRadioGroup(els.lengthContainer, state.length);
      if (els.lengthDisplay) els.lengthDisplay.textContent = state.length || '';
    }

    function updateRadioGroup(cont, selectedValue) {
      if (!cont) return;
      var buttons = cont.querySelectorAll('.bundle-builder__option-btn');
      buttons.forEach(function (btn) {
        var selected = btn.dataset.value === selectedValue;
        btn.setAttribute('aria-pressed', selected.toString());
        btn.setAttribute('tabindex', selected ? '0' : '-1');
      });
    }

    function updateWaistAvailability() {
      if (!els.waistContainer) return;
      var buttons = els.waistContainer.querySelectorAll('.bundle-builder__option-btn');
      buttons.forEach(function (btn) {
        var waist = btn.dataset.value;
        var hasAvailable = false;
        if (!hasInseam) {
          /* No inseam option — check waist + color only */
          options.colors.some(function (color) {
            var key = buildLookupKey(state.fit, waist, null, color);
            if (variants[key] && variants[key].available) { hasAvailable = true; return true; }
          });
        } else if (options.lengths && options.colors) {
          options.lengths.some(function (len) {
            return options.colors.some(function (color) {
              var key = buildLookupKey(state.fit, waist, len, color);
              if (variants[key] && variants[key].available) { hasAvailable = true; return true; }
            });
          });
        }
        btn.disabled = !hasAvailable;
        btn.setAttribute('aria-disabled', (!hasAvailable).toString());
      });
    }

    function updateLengthAvailability() {
      if (!els.lengthContainer) return;
      var buttons = els.lengthContainer.querySelectorAll('.bundle-builder__option-btn');
      buttons.forEach(function (btn) {
        var len = btn.dataset.value;
        var hasAvailable = false;
        if (options.colors) {
          options.colors.some(function (color) {
            var key = buildLookupKey(state.fit, state.waist || (options.waists && options.waists[0]), len, color);
            if (variants[key] && variants[key].available) { hasAvailable = true; return true; }
          });
        }
        btn.disabled = !hasAvailable;
        btn.setAttribute('aria-disabled', (!hasAvailable).toString());
      });
      ensureOneTabTarget(els.lengthContainer);
    }

    function updateColorAvailability() {
      var sizeReady = state.waist && (!hasInseam || state.length);

      /* First pass: clear selections for colors that are no longer available.
         Must run before rendering swatches so isSelected reflects cleared state. */
      if (sizeReady) {
        var packSize = getActiveBundleSize();
        var selectionsCleared = false;
        for (var si = 0; si < packSize; si++) {
          var selectedColor = state.colors[si];
          if (selectedColor) {
            var key = buildLookupKey(state.fit, state.waist, state.length, selectedColor);
            if (!variants[key] || !variants[key].available) {
              state.colors[si] = null;
              selectionsCleared = true;
            }
          }
        }
        if (selectionsCleared) {
          updateColorSlotUI();
          updateBuilderImages();
        }
      }

      /* Second pass: show/hide and update each swatch */
      els.colorSwatchContainers.forEach(function (cont, slotIndex) {
        var swatchBtns = cont.querySelectorAll('.bundle-builder__swatch');
        swatchBtns.forEach(function (swatch) {
          var color = swatch.dataset.color;
          var available = true;
          if (sizeReady) {
            var key = buildLookupKey(state.fit, state.waist, state.length, color);
            available = !!(variants[key] && variants[key].available);
          }

          /* Hide swatches with no valid variant for the current size/inseam */
          swatch.style.display = available ? '' : 'none';

          if (!available) {
            swatch.disabled = true;
            swatch.setAttribute('aria-disabled', 'true');
            swatch.setAttribute('aria-pressed', 'false');
            swatch.setAttribute('tabindex', '-1');
            return; /* skip duplicate/selected logic for hidden swatches */
          }

          var isDuplicate = false;
          if (!config.allowDuplicateColors) {
            state.colors.forEach(function (sel, idx) {
              if (idx !== slotIndex && sel === color) isDuplicate = true;
            });
          }
          var isSelected = state.colors[slotIndex] === color;
          var isDisabled = isDuplicate && !isSelected;
          swatch.disabled = isDisabled;
          swatch.setAttribute('aria-disabled', isDisabled.toString());
          if (isDisabled) {
            swatch.setAttribute('aria-label', color + ', already selected');
          } else {
            swatch.setAttribute('aria-label', color);
          }
          swatch.setAttribute('aria-pressed', isSelected.toString());
          swatch.setAttribute('tabindex', isSelected ? '0' : '-1');
        });
        ensureOneTabTarget(cont);
      });
    }

    function updateColorSlotUI() {
      els.colorSlots.forEach(function (slot, index) {
        var display = slot.querySelector('[data-color-display]');
        var color = state.colors[index];
        slot.classList.toggle('is-complete', color !== null);
        slot.classList.toggle('is-active', index === state.activeColorSlot);
        if (display) display.textContent = color || '';
      });
    }

    /* ============================
       GALLERY
       ============================ */
    function initGallery() {
      if (!els.gallery) return;

      /* Thumb arrows — show/hide based on active image index, click advances prev/next image */
      var thumbsArray = Array.from(els.galleryThumbs);

      function getActiveThumbIndex() {
        var activeThumb = container.querySelector('.bundle-builder__gallery-thumb.is-active');
        return activeThumb ? parseInt(activeThumb.dataset.galleryThumb, 10) : 0;
      }

      function updateThumbArrows() {
        if (!els.thumbArrowPrev || !els.thumbArrowNext) return;
        var idx = getActiveThumbIndex();
        els.thumbArrowPrev.classList.toggle('hide', idx <= 0);
        els.thumbArrowNext.classList.toggle('hide', idx >= thumbsArray.length - 1);
      }

      /* Set correct initial arrow state immediately — no need to wait for image load */
      updateThumbArrows();

      if (els.thumbArrowPrev) {
        els.thumbArrowPrev.addEventListener('click', function () {
          var idx = getActiveThumbIndex();
          var prevIdx = Math.max(idx - 1, 0);
          if (prevIdx !== idx) thumbsArray[prevIdx].click();
        });
      }

      if (els.thumbArrowNext) {
        els.thumbArrowNext.addEventListener('click', function () {
          var idx = getActiveThumbIndex();
          var nextIdx = Math.min(idx + 1, thumbsArray.length - 1);
          if (nextIdx !== idx) thumbsArray[nextIdx].click();
        });
      }

      thumbsArray.forEach(function (thumb) {
        // Set initial aria-pressed state
        thumb.setAttribute('aria-pressed', thumb.classList.contains('is-active') ? 'true' : 'false');
        thumb.addEventListener('click', function () {
          var src = thumb.dataset.src;
          var alt = thumb.dataset.alt;
          if (els.galleryMainImg) {
            els.galleryMainImg.src = src;
            els.galleryMainImg.alt = alt;
            /* Keep photoswipe zoom in sync with the displayed image */
            if (thumb.dataset.zoomSrc) {
              els.galleryMainImg.setAttribute('data-photoswipe-src', thumb.dataset.zoomSrc);
            }
            if (thumb.dataset.zoomW) {
              els.galleryMainImg.setAttribute('data-photoswipe-width', thumb.dataset.zoomW);
            }
            if (thumb.dataset.zoomH) {
              els.galleryMainImg.setAttribute('data-photoswipe-height', thumb.dataset.zoomH);
            }
          }
          els.galleryThumbs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-pressed', 'false');
          });
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-pressed', 'true');
          /* Scroll active thumb into view within the thumb container */
          thumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          /* Update arrow visibility based on new active index */
          updateThumbArrows();
          /* Sync mobile dots to match active thumb */
          syncGalleryDots(parseInt(thumb.dataset.galleryThumb, 10));
        });
      });

      /* Helper: set active dot by index */
      function syncGalleryDots(index) {
        els.galleryDots.forEach(function (dot) {
          var active = parseInt(dot.dataset.galleryDot, 10) === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }

      /* Mobile dot click — swap main image and sync thumbs */
      els.galleryDots.forEach(function (dot) {
        dot.setAttribute('aria-pressed', dot.classList.contains('is-active') ? 'true' : 'false');
        dot.addEventListener('click', function () {
          var index = parseInt(dot.dataset.galleryDot, 10);
          var thumb = container.querySelector('[data-gallery-thumb="' + index + '"]');
          if (thumb) {
            thumb.click(); /* Reuse thumb click handler — updates image, zoom attrs, dots */
          }
        });
      });

      /* Mobile swipe on gallery main image.
         Pointer events are used where supported; touch events are a fallback for
         DevTools emulation and older browsers where pointer events may not fire correctly. */
      if (els.galleryMain) {
        var startX = 0, startY = 0, isTracking = false, directionLocked = false, isHorizontalSwipe = false;
        var pointerHandled = false; /* Prevent double-handling when both pointer + touch fire */

        function onStart(clientX, clientY) {
          startX = clientX;
          startY = clientY;
          isTracking = true;
          directionLocked = false;
          isHorizontalSwipe = false;
        }

        function onMove(clientX, clientY) {
          if (!isTracking || directionLocked) return;
          var dx = Math.abs(clientX - startX);
          var dy = Math.abs(clientY - startY);
          if (dx > 8 || dy > 8) {
            directionLocked = true;
            isHorizontalSwipe = dx > dy;
          }
        }

        function onEnd(clientX, clientY) {
          if (!isTracking) return;
          isTracking = false;
          /* No tap-to-zoom — zoom button is the sole zoom trigger, matching default PDP */
          if (!directionLocked) return;
          if (!isHorizontalSwipe) return;
          var dx = clientX - startX;
          var dy = clientY - startY;
          if (Math.abs(dx) < 30 || Math.abs(dy) > Math.abs(dx)) return;
          var thumbs = Array.from(els.galleryThumbs);
          if (thumbs.length < 2) return;
          var activeThumb = container.querySelector('.bundle-builder__gallery-thumb.is-active');
          var currentIndex = activeThumb ? parseInt(activeThumb.dataset.galleryThumb, 10) : 0;
          var nextIndex = dx < 0
            ? Math.min(currentIndex + 1, thumbs.length - 1)
            : Math.max(currentIndex - 1, 0);
          if (nextIndex !== currentIndex) {
            thumbs[nextIndex].click();
          }
        }

        /* Pointer events (real devices, modern browsers) */
        els.galleryMain.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;
          pointerHandled = true;
          onStart(e.clientX, e.clientY);
          try { els.galleryMain.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        });
        els.galleryMain.addEventListener('pointermove', function (e) {
          if (!pointerHandled) return;
          onMove(e.clientX, e.clientY);
        });
        els.galleryMain.addEventListener('pointerup', function (e) {
          if (!pointerHandled) return;
          pointerHandled = false;
          onEnd(e.clientX, e.clientY);
        });
        els.galleryMain.addEventListener('pointercancel', function () {
          pointerHandled = false;
          isTracking = false;
        });

        /* Touch event fallback — fires when pointer events didn't handle the gesture */
        els.galleryMain.addEventListener('touchstart', function (e) {
          if (pointerHandled) return;
          onStart(e.touches[0].clientX, e.touches[0].clientY);
        }, { passive: true });
        els.galleryMain.addEventListener('touchmove', function (e) {
          if (pointerHandled) return;
          onMove(e.touches[0].clientX, e.touches[0].clientY);
          if (isHorizontalSwipe) e.preventDefault();
        }, { passive: false });
        els.galleryMain.addEventListener('touchend', function (e) {
          if (pointerHandled) return;
          var t = e.changedTouches[0];
          onEnd(t.clientX, t.clientY);
        }, { passive: true });
      }
    }

    function switchToBuilder() {
      if (!state.showGallery) return;
      state.showGallery = false;
      if (els.gallery) els.gallery.style.display = 'none';
      if (els.builderGrid) els.builderGrid.style.display = '';
      if (els.builderSlider) els.builderSlider.style.display = '';
      updateBuilderImages();
    }

    /* ============================
       IMAGE UPDATES
       ============================ */
    function getVariantImage(index) {
      var color = state.colors[index];
      if (color && state.waist && (!hasInseam || state.length)) {
        var key = buildLookupKey(state.fit, state.waist, state.length, color);
        var variant = variants[key];
        if (variant && variant.image) {
          return {
            src: variant.image,
            zoom: variant.image_zoom || variant.image,
            alt: variant.image_alt || variant.product_title + ' in ' + color
          };
        }
      }
      return null;
    }

    function renderImage(cont, index, cardEl) {
      /* ── Persistent-element strategy ──────────────────────────────────────
         Never replace innerHTML. Instead, locate or create a single <img>
         inside cont and update its attributes in place. This prevents the
         container from going empty even for one frame, which was collapsing
         its height and causing the iOS page-bounce on every color / size /
         inseam change.
         ───────────────────────────────────────────────────────────────────── */
      var varImg = getVariantImage(index);
      var color  = state.colors[index];

      /* Find the persistent <img>, or create it once. */
      var imgEl = cont.querySelector('img.bundle-builder__pair-img');
      if (!imgEl) {
        imgEl = document.createElement('img');
        imgEl.className = 'bundle-builder__pair-img';
        imgEl.width  = 1200;
        imgEl.height = 1600;
        imgEl.loading = 'lazy';
        cont.innerHTML = ''; /* clear any legacy text nodes only on first init */
        cont.appendChild(imgEl);
      }

      if (varImg) {
        /* Real variant image — keep placeholder styling until new src is decoded
           so the shaded placeholder never flashes clear before the image arrives */
        imgEl.alt = varImg.alt;
        imgEl.setAttribute('data-zoom-src', varImg.zoom);
        imgEl.style.opacity = '';

        if (imgEl.src !== varImg.src) {
          /* Src is changing — clear any pending listener from a previous rapid selection
             before adding the new one, preventing stale listener accumulation */
          if (imgEl._revealOnLoad) {
            imgEl.removeEventListener('load', imgEl._revealOnLoad);
            imgEl.removeEventListener('error', imgEl._revealOnLoad);
          }
          /* Defer class removal until load (or error) */
          imgEl._revealOnLoad = function () {
            imgEl.classList.remove('bundle-builder__pair-img--placeholder');
            imgEl.removeEventListener('load', imgEl._revealOnLoad);
            imgEl.removeEventListener('error', imgEl._revealOnLoad);
            imgEl._revealOnLoad = null;
          };
          imgEl.addEventListener('load', imgEl._revealOnLoad);
          imgEl.addEventListener('error', imgEl._revealOnLoad); /* reveal on error so it never stays shaded permanently */
          imgEl.src = varImg.src;
          if (imgEl.complete) { imgEl._revealOnLoad(); } /* already cached — load event may not fire */
        } else {
          /* Same src — already displayed, just ensure class is removed */
          imgEl.classList.remove('bundle-builder__pair-img--placeholder');
        }

        var interactiveEl = cardEl || cont;
        if (cardEl) cardEl.classList.add('has-image');
        interactiveEl.setAttribute('role', 'button');
        interactiveEl.setAttribute('tabindex', '0');
        interactiveEl.setAttribute('aria-label', 'View ' + (color || 'item') + ' photos');

      } else if (config.placeholderImage) {
        /* Placeholder image — clear any pending reveal listener so it can't fire
           and incorrectly remove the placeholder class when the old src resolves */
        if (imgEl._revealOnLoad) {
          imgEl.removeEventListener('load', imgEl._revealOnLoad);
          imgEl.removeEventListener('error', imgEl._revealOnLoad);
          imgEl._revealOnLoad = null;
        }
        imgEl.src = config.placeholderImage;
        imgEl.alt = 'Select a color';
        imgEl.removeAttribute('data-zoom-src');
        imgEl.classList.add('bundle-builder__pair-img--placeholder');
        imgEl.style.opacity = '';

        var interactiveEl = cardEl || cont;
        if (cardEl) cardEl.classList.add('has-image');
        interactiveEl.removeAttribute('role');
        interactiveEl.removeAttribute('tabindex');
        interactiveEl.removeAttribute('aria-label');

      } else {
        /* No image and no placeholder — clear pending listener and hide img */
        if (imgEl._revealOnLoad) {
          imgEl.removeEventListener('load', imgEl._revealOnLoad);
          imgEl.removeEventListener('error', imgEl._revealOnLoad);
          imgEl._revealOnLoad = null;
        }
        imgEl.style.opacity = '0';
        imgEl.alt = '';

        var interactiveEl = cardEl || cont;
        if (cardEl) cardEl.classList.remove('has-image');
        interactiveEl.removeAttribute('role');
        interactiveEl.removeAttribute('tabindex');
        interactiveEl.removeAttribute('aria-label');
      }
    }

    function updateBuilderImages() {
      if (state.showGallery) return;
      els.slideImages.forEach(function (imgEl, index) { renderImage(imgEl, index, null); });
      els.pairImages.forEach(function (imgEl, index) { renderImage(imgEl, index, els.pairCards[index]); });
    }

    function updateImageGrid() { updateBuilderImages(); }

    /* ============================
       SLIDER
       ============================ */
    function goToSlide(index, silent) {
      var packSize = getActiveBundleSize();
      if (index < 0) index = packSize - 1;
      if (index >= packSize) index = 0;
      state.currentSlide = index;
      els.slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
      if (els.sliderTrack) els.sliderTrack.style.transform = 'translateX(-' + (index * 100) + '%)';
      els.sliderDots.forEach(function (dot, i) {
        var isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive.toString());
      });
      if (!silent) announce(config.itemLabel + ' #' + (index + 1));
    }

    function initSlider() {
      if (els.sliderPrev) els.sliderPrev.addEventListener('click', function () { goToSlide(state.currentSlide - 1); });
      if (els.sliderNext) els.sliderNext.addEventListener('click', function () { goToSlide(state.currentSlide + 1); });
      els.sliderDots.forEach(function (dot) {
        dot.addEventListener('click', function () { goToSlide(parseInt(dot.dataset.dot, 10)); });
        // Set initial aria-pressed state (liquid sets is-active on first dot)
        dot.setAttribute('aria-pressed', dot.classList.contains('is-active') ? 'true' : 'false');
      });
      if (els.sliderTrack) {
        var touchStartX = 0, touchStartY = 0, isDragging = false;
        els.sliderTrack.addEventListener('touchstart', function (e) {
          touchStartX = e.touches[0].clientX; touchStartY = e.touches[0].clientY; isDragging = true;
        }, { passive: true });
        els.sliderTrack.addEventListener('touchend', function (e) {
          if (!isDragging) return;
          isDragging = false;
          var dx = e.changedTouches[0].clientX - touchStartX;
          var dy = e.changedTouches[0].clientY - touchStartY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
            if (dx < 0) goToSlide(state.currentSlide + 1);
            else goToSlide(state.currentSlide - 1);
          }
        }, { passive: true });
      }
    }

    /* ============================
       SELECTION HANDLERS
       ============================ */
    function selectColor(slotIndex, color) {
      if (config.singleColorMode) {
        var packSize = getActiveBundleSize();
        for (var i = 0; i < packSize; i++) {
          state.colors[i] = color;
        }
        var displaySpan = container.querySelector('[data-color-display="0"]');
        if (displaySpan) displaySpan.textContent = color;
        if (els.singleColorImage) {
          els.singleColorImage.style.display = '';
          renderImage(els.singleColorImage, 0, null);
        }
      } else {
        state.colors[slotIndex] = color;
      }
      /* No announce() here — the swatch button's own aria-pressed="true" state change
         communicates the selection to screen readers ("Color Name, button, pressed").
         A redundant live region announce on top of that also caused iOS WebKit to scroll
         the page toward the sr-announce element on every tap. */
      if (state.showGallery) switchToBuilder();
      updateColorSlotUI();
      updateColorAvailability();
      updateBuilderImages();
      if (!config.singleColorMode) updateApplyToAllBtn();
      validate();
      if (!config.singleColorMode) goToSlide(slotIndex, true);
    }

    /* ============================
       APPLY TO ALL
       ============================ */
    function createApplyToAllBtn() {
      if (!config.showApplyToAll) return;
      var firstSlot = els.colorSlots[0];
      if (!firstSlot) return;

      /* Target the div wrapper used for the first slot's header row */
      var headerEl = firstSlot.querySelector('.bundle-builder__color-slot-header');
      if (!headerEl) return;

      /* Guard: prevent duplicate button if initBundleBuilder runs again on same container */
      if (headerEl.querySelector('[data-apply-to-all]')) {
        els.applyToAllBtn = headerEl.querySelector('[data-apply-to-all]');
        return;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__apply-to-all';
      btn.setAttribute('data-apply-to-all', '');
      btn.textContent = 'Apply Color to All';
      btn.setAttribute('aria-label', 'Apply Color to All slots');
      btn.style.visibility = 'hidden'; /* space always reserved; hidden until first color is picked */
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        applyToAll();
      });

      headerEl.appendChild(btn);
      els.applyToAllBtn = btn;
    }

    function applyToAll() {
      var color = state.colors[0];
      if (!color) return;
      var packSize = getActiveBundleSize();

      for (var i = 1; i < packSize; i++) {
        state.colors[i] = color;
      }

      announce(color + ' applied to all ' + packSize + ' slots');
      if (state.showGallery) switchToBuilder();
      updateColorSlotUI();
      updateColorAvailability();
      updateBuilderImages();
      updateApplyToAllBtn();
      validate();
    }

    function updateApplyToAllBtn() {
      if (!els.applyToAllBtn) return;
      var packSize = getActiveBundleSize();
      var firstColor = state.colors[0];

      /* Only show when first color is picked AND there are multiple slots */
      var shouldShow = !!firstColor && packSize > 1;
      els.applyToAllBtn.style.visibility = shouldShow ? '' : 'hidden';

      if (firstColor) {
        els.applyToAllBtn.setAttribute('aria-label', 'Apply Color to All: ' + firstColor + ' applied to all slots');
      }
    }

    function setActiveColorSlot(index) {
      state.activeColorSlot = index;
      updateColorSlotUI();
    }

    /* ============================
       VALIDATION
       ============================ */
    function validate() {
      var packSize = getActiveBundleSize();

      /* Check in visual page order: fit → inseam → size → colors.
         Only the FIRST missing option is captured — the button shows
         one clear instruction at a time. */
      var missing = null;

      if (config.showFit && !state.fit) {
        missing = { label: config.fitLabel || 'Fit', step: 'fit' };
      } else if (hasInseam && !state.length) {
        missing = { label: config.lengthLabel || 'Inseam', step: 'size' };
      } else if (!state.waist) {
        missing = { label: config.waistLabel || 'Size', step: 'size' };
      } else {
        for (var i = 0; i < packSize; i++) {
          if (state.colors[i] === null) {
            var colorLabel = config.colorLabel || 'Color';
            /* Single color mode or pack of 1: "Select Color"
               Multi-color mode: "Select Color 2" (slot-specific) */
            var prompt = (config.singleColorMode || packSize === 1)
              ? colorLabel
              : colorLabel + ' ' + (i + 1);
            missing = { label: prompt, step: 'colors', slot: i };
            break;
          }
        }
      }

      var valid = !missing;

      /* Update button — class-based, never HTML disabled, so it stays
         clickable for the scroll-to-help behaviour in addToCart(). */
      els.addBtn.classList.toggle('is-incomplete', !valid);
      els.addBtn.setAttribute('aria-disabled', (!valid).toString());
      els.addBtnText.textContent = valid
        ? (config.buttonText || 'ADD TO CART')
        : 'Select ' + missing.label;

      _firstMissing = missing;
      updateInventoryStatus();
      return valid;
    }

    /* ============================
       INVENTORY STATUS
       Runs after every selection change via validate().
       Shows "Low stock — N bundles left" or "Out of stock"
       only when size + inseam are selected (context exists).
       Pack-size aware: divides available units by active pack size.
       Duplicate color selections are accounted for correctly.
       ADA: role="status" + aria-live="polite" on the container element.
       ============================ */
    /* iOS WebKit scrolls toward any aria-live region when its text content
       changes, even if the element is in normal document flow. The cache
       below ensures DOM writes only happen when the message or dot state
       actually changes — preventing spurious aria-live fires on every click. */
    var _inventoryCache = { msg: null, dotClass: null };

    function updateInventoryStatus() {
      if (!els.inventoryStatus) return;

      /* ── Calculate target state (no DOM writes yet) ── */
      var targetMsg;
      var targetDotClass;

      var sizeComplete = state.waist && (!hasInseam || state.length) && (!config.showFit || state.fit);
      if (!sizeComplete) {
        targetMsg = 'In stock, ready to ship';
        targetDotClass = 'bundle-inventory__dot';
      } else {
        var packSize = getActiveBundleSize();
        var threshold = (config.inventoryThreshold != null) ? config.inventoryThreshold : 10;

        var colorUsage = {};
        var filledCount = 0;
        for (var i = 0; i < packSize; i++) {
          if (!state.colors[i]) continue;
          filledCount++;
          var usageKey = buildLookupKey(state.fit, state.waist, state.length, state.colors[i]);
          colorUsage[usageKey] = (colorUsage[usageKey] || 0) + 1;
        }

        if (filledCount === 0) {
          targetMsg = 'In stock, ready to ship';
          targetDotClass = 'bundle-inventory__dot';
        } else {
          var availableBundles = Infinity;
          for (var cKey in colorUsage) {
            var cVariant = variants[cKey];
            var qty = (cVariant && cVariant.inventory_quantity > 0) ? cVariant.inventory_quantity : Infinity;
            var bundlesFromThisColor = Math.floor(qty / colorUsage[cKey]);
            if (bundlesFromThisColor < availableBundles) availableBundles = bundlesFromThisColor;
          }

          if (availableBundles === Infinity) {
            targetMsg = 'In stock, ready to ship';
            targetDotClass = 'bundle-inventory__dot';
          } else {
            var rawItemCount = availableBundles * packSize;
            if (rawItemCount <= 0) {
              targetMsg = 'Out of stock for selected options';
              targetDotClass = 'bundle-inventory__dot bundle-inventory__dot--out';
            } else if (rawItemCount <= threshold) {
              var bundleWord = availableBundles === 1 ? 'bundle' : 'bundles';
              targetMsg = 'Low stock \u2013 ' + availableBundles + ' ' + bundleWord + ' left';
              targetDotClass = 'bundle-inventory__dot bundle-inventory__dot--low';
            } else {
              targetMsg = 'In stock, ready to ship';
              targetDotClass = 'bundle-inventory__dot';
            }
          }
        }
      }

      /* ── Only write to DOM when state actually changed ──
         Skipping identical writes prevents the aria-live region from
         firing on iOS WebKit and scrolling the page on every color click. */
      if (targetMsg === _inventoryCache.msg && targetDotClass === _inventoryCache.dotClass) return;

      _inventoryCache.msg = targetMsg;
      _inventoryCache.dotClass = targetDotClass;

      els.inventoryStatus.style.display = '';
      els.inventoryDot.className = targetDotClass;
      els.inventoryText.textContent = targetMsg;
      els.inventoryStatus.setAttribute('aria-label', targetMsg);
    }

    /* ============================
       VARIANT LOOKUP
       ============================ */
    function buildLookupKey(fit, waist, length, color) {
      /* When the source product has no inseam option, length is omitted from
         the key so JS keys match the keys built in the liquid template. */
      if (config.showFit && fit) {
        if (hasInseam) return (fit + '|' + waist + '|' + length + '|' + color).toLowerCase();
        return (fit + '|' + waist + '|' + color).toLowerCase();
      }
      if (hasInseam) return (waist + '|' + length + '|' + color).toLowerCase();
      return (waist + '|' + color).toLowerCase();
    }

    function getVariantIds() {
      var ids = [];
      var packSize = getActiveBundleSize();
      var colorUsage = {}; /* lookup key → times used in this bundle */

      for (var i = 0; i < packSize; i++) {
        var key = buildLookupKey(state.fit, state.waist, state.length, state.colors[i]);
        var variant = variants[key];
        if (!variant || !variant.id || !variant.available) return null;

        colorUsage[key] = (colorUsage[key] || 0) + 1;

        /*
         * If inventory is tracked, ensure enough units exist for
         * repeated selections of the same color.
         * e.g. 3× Navy when only 2 Navy in stock → block.
         */
        if (variant.inventory_quantity > 0 && variant.inventory_quantity < colorUsage[key]) {
          return null;
        }

        ids.push(variant.id);
      }
      return ids;
    }

    /* ============================
       CART
       ============================ */
    function addToCart() {
      /* Prevent double-tap while a request is in flight */
      if (els.addBtn.classList.contains('is-loading')) return;

      if (!validate()) {
        /* ── Scroll-to-help: guide the customer to the missing option ── */
        if (_firstMissing) {
          var stepEl = container.querySelector('[data-step="' + _firstMissing.step + '"]');
          if (stepEl) {
            var header = document.querySelector('.site-header, [data-section-type="header"]');
            var offset = (header ? header.offsetHeight : 0) + 16;
            var top = stepEl.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
          /* In multi-color mode, activate the empty slot so it's visually clear */
          if (_firstMissing.slot != null && !config.singleColorMode) {
            setActiveColorSlot(_firstMissing.slot);
          }
          announce('Please select ' + _firstMissing.label + ' to continue.');
        }
        return;
      }
      var variantIds = getVariantIds();
      if (!variantIds) {
        showError('One or more selections are unavailable. Please adjust your choices.');
        announce('Error: one or more selections are unavailable.');
        return;
      }
      var bundleId = 'bb-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8);
      var tier = getCurrentTier();
      var packSize = getActiveBundleSize();

      var items = variantIds.map(function (id, index) {
        var props = {
          'Bundle': ((config.bundleTitle || 'Bundle').replace(/\bbundle\b/gi, '').replace(/\s{2,}/g, ' ').trim()) || config.bundleTitle || 'Bundle',
          'Item': (index + 1) + ' of ' + packSize,
          '_bundle_id': bundleId,
          '_bundle_title': config.bundleTitle || 'Bundle',
          '_bundle_type': config.bundleType,
          '_bundle_size': packSize.toString(),
          '_bundle_index': (index + 1).toString(),
          '_bundle_url': window.location.pathname + window.location.search
        };
        if (tier) {
          props['_discount_type'] = tier.type;
          props['_discount_value'] = tier.value.toString();
          props['_shell_product_gid'] = config.shellProductGid || '';
        }
        return { id: id, quantity: 1, properties: props };
      });

      setLoading(true);
      hideError();
      announce('Adding bundle to bag.');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (err) {
              throw new Error(err.description || err.message || 'Failed to add to cart');
            });
          }
          return response.json();
        })
        .then(function (responseData) {
          setLoading(false);
          announce('Bundle added to bag successfully.');
          document.dispatchEvent(new CustomEvent('bundleBuilder:added', {
            detail: { bundleId: bundleId, bundleType: config.bundleType, items: responseData.items || responseData }
          }));
          refreshCartAndOpen();
        })
        .catch(function (err) {
          setLoading(false);
          showError(err.message || 'Something went wrong. Please try again.');
          announce('Error: ' + (err.message || 'Something went wrong.'));
          console.error('[BundleBuilder] Cart error:', err);
        });
    }

    function refreshCartAndOpen() {
      var ampHandled = false;
      try {
        if (typeof window.AMP_CART_UPDATED === 'function' && window.AMP_API && typeof window.AMP_API.OPEN_CART === 'function') {
          window.AMP_CART_UPDATED();
          window.AMP_API.OPEN_CART();
          ampHandled = true;
        }
      } catch (e) {}
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!ampHandled) {
            try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (e) {}
            try {
              if (window.theme && window.theme.CartDrawer) {
                var drawer = new window.theme.CartDrawer();
                drawer.buildCart(cart, true);
              }
            } catch (e) {}
          }
          updateHeaderCartCount(cart.item_count);
        })
        .catch(function () { if (!ampHandled) window.location.href = '/cart'; });
    }

    function updateHeaderCartCount(count) {
      if (!count && count !== 0) return;
      try {
        var countEls = document.querySelectorAll('.cart-link__count, .cart-count, [data-cart-count]');
        countEls.forEach(function (el) { el.textContent = count; });
        var bubble = document.querySelector('.cart-link__bubble');
        if (bubble) bubble.classList.toggle('cart-link__bubble--visible', count > 0);
      } catch (e) {}
    }

    /* ============================
       UI HELPERS
       ============================ */
    function setLoading(loading) {
      els.addBtn.classList.toggle('is-loading', loading);
      if (loading) {
        els.addBtn.classList.remove('is-incomplete');
        els.addBtn.setAttribute('aria-disabled', 'true');
        els.addBtnText.textContent = 'Adding...';
      } else {
        /* Let validate() restore the correct button text + state */
        validate();
      }
    }

    function showError(msg) { els.errorEl.textContent = msg; els.errorEl.hidden = false; }
    function hideError() { els.errorEl.textContent = ''; els.errorEl.hidden = true; }

    function escapeAttr(str) {
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    function escapeHTML(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function ensureOneTabTarget(cont) {
      if (!cont) return;
      var has = cont.querySelector('[tabindex="0"]');
      if (!has) {
        var first = cont.querySelector('button:not([disabled])');
        if (first) first.setAttribute('tabindex', '0');
      }
    }

    /* ============================
       EVENT BINDINGS & INIT
       ============================ */
    els.addBtn.addEventListener('click', addToCart);

    [els.fitContainer, els.waistContainer, els.lengthContainer].forEach(function (group) {
      if (!group) return;
      var first = group.querySelector('.bundle-builder__option-btn:not([disabled])');
      if (first) first.setAttribute('tabindex', '0');
    });

    setActiveColorSlot(0);
    updateColorSlotUI();

    config.bundleSize = getActiveBundleSize();
    renderTierSelector();
    updatePriceDisplay();

    if (state.showGallery) {
      initGallery();
    } else {
      if (els.builderGrid) els.builderGrid.style.display = '';
      if (els.builderSlider) els.builderSlider.style.display = '';
      if (els.gallery) els.gallery.style.display = 'none';
      updateBuilderImages();
    }

    initSlider();
    createApplyToAllBtn();
    validate();

    /* ============================
       PHOTOSWIPE ZOOM (uses theme's existing PhotoSwipe)
       ============================ */
    function initZoom() {
      /* Click + keyboard handler on grid cards */
      els.pairCards.forEach(function (card) {
        card.addEventListener('click', function () {
          var img = card.querySelector('.bundle-builder__pair-img:not(.bundle-builder__pair-img--placeholder)');
          if (!img) return;
          var index = parseInt(card.dataset.pairCard, 10);
          openColorLightbox(index);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            var img = card.querySelector('.bundle-builder__pair-img:not(.bundle-builder__pair-img--placeholder)');
            if (!img) return;
            var index = parseInt(card.dataset.pairCard, 10);
            openColorLightbox(index);
          }
        });
      });

      /* Click + keyboard handler on slider images */
      els.slideImages.forEach(function (slideImg) {
        slideImg.addEventListener('click', function () {
          var img = slideImg.querySelector('.bundle-builder__pair-img:not(.bundle-builder__pair-img--placeholder)');
          if (!img) return;
          var index = parseInt(slideImg.dataset.slideImage, 10);
          openColorLightbox(index);
        });
        slideImg.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            var img = slideImg.querySelector('.bundle-builder__pair-img:not(.bundle-builder__pair-img--placeholder)');
            if (!img) return;
            var index = parseInt(slideImg.dataset.slideImage, 10);
            openColorLightbox(index);
          }
        });
      });

      /* Shell gallery zoom — bound to .js-photoswipe__zoom button, matching media.liquid pattern */
      var zoomBtn = container.querySelector('.js-photoswipe__zoom');
      if (zoomBtn) {
        zoomBtn.addEventListener('click', function () {
          openShellGalleryZoom();
        });
      }
    }

    function collectZoomItems() {
      var items = [];
      var packSize = getActiveBundleSize();
      for (var i = 0; i < packSize; i++) {
        var img = getVariantImage(i);
        if (img && img.zoom) {
          items.push({ src: img.zoom, w: 1800, h: 2400, title: img.alt });
        }
      }
      return items;
    }

    function openColorLightbox(slotIndex) {
      var color = state.colors[slotIndex];
      if (!color) return;

      /* Require same state conditions as getVariantImage — need a resolvable variant */
      if (!state.waist) return;
      if (hasInseam && !state.length) return;

      /* Look up the current slot's variant and read its image_pos.
         image_pos is the featured_image.position integer set in the variant JSON.
         This is fully option-agnostic — works for any combination of options. */
      var lookupKey = buildLookupKey(state.fit, state.waist, state.length, color);
      var variant = variants[lookupKey];
      if (!variant || variant.image_pos == null || variant.image_pos < 0) {
        openPhotoSwipe(slotIndex);
        return;
      }

      var colorImagesData = config.colorImages && config.colorImages[String(variant.image_pos)];

      /* No multi-image data for this color — fall back to single variant image */
      if (!colorImagesData || colorImagesData.length === 0) {
        openPhotoSwipe(slotIndex);
        return;
      }

      var pswpEl = document.querySelector('.pswp');
      if (!pswpEl || typeof PhotoSwipe === 'undefined') {
        /* PhotoSwipe not available — open first image in new tab */
        window.open(colorImagesData[0].zoom || colorImagesData[0].src, '_blank');
        return;
      }

      /* Match theme.Photoswipe exactly: pass real image dimensions and set
         initialZoomLevel: 0.5 per item. On a ~390px phone with a 2500px image,
         0.5 renders at 1250px — fills and overflows the screen exactly as the
         default PDP zoom does. scaleMode: 'zoom' matches theme options. */
      var items = colorImagesData.map(function (img) {
        return {
          src: img.zoom || img.src,
          w: img.w || 1200,
          h: img.h || 1500,
          title: img.alt || color,
          initialZoomLevel: 0.5
        };
      });

      var options = {
        index: 0,
        bgOpacity: 0.95,
        showHideOpacity: true,
        history: false,
        closeOnScroll: false,
        loop: items.length > 1,
        scaleMode: 'zoom',
        allowPanToNext: false,
        pinchToClose: false,
        tapToToggleControls: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: false,
        preloaderEl: false
      };

      var gallery = new PhotoSwipe(pswpEl, PhotoSwipeUI_Default, items, options);
      gallery.init();

      /* Full-screen scroll lock — applied to ALL devices, not just iOS.
         pswp-open-in-ios hides body content and locks scroll, making .pswp fill
         the viewport. --window-inner-height is needed on iOS (Safari address bar
         resize) but is harmless on Android. Without this, position:absolute on
         .pswp means the lightbox doesn't fill the viewport on Android when scrolled. */
      var initialScrollPos = window.scrollY;
      document.documentElement.style.setProperty('--window-inner-height', window.innerHeight + 'px');
      document.documentElement.classList.add('pswp-open-in-ios');
      function syncHeight() {
        document.documentElement.style.setProperty('--window-inner-height', window.innerHeight + 'px');
      }
      window.addEventListener('resize', syncHeight);
      gallery.listen('destroy', function () {
        document.documentElement.classList.remove('pswp-open-in-ios');
        window.scrollTo(0, initialScrollPos);
        window.removeEventListener('resize', syncHeight);
      });
    }

    function openPhotoSwipe(startIndex) {
      var items = collectZoomItems();
      if (items.length === 0) return;

      /* Clamp startIndex to valid range */
      if (startIndex >= items.length) startIndex = 0;

      var pswpEl = document.querySelector('.pswp');
      if (!pswpEl || typeof PhotoSwipe === 'undefined') {
        /* PhotoSwipe not available — fall back to opening image in new tab */
        if (items[startIndex]) window.open(items[startIndex].src, '_blank');
        return;
      }

      var pswpOptions = {
        index: startIndex,
        bgOpacity: 0.95,
        showHideOpacity: true,
        history: false,
        closeOnScroll: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: items.length > 1,
        preloaderEl: true
      };

      var gallery = new PhotoSwipe(pswpEl, PhotoSwipeUI_Default, items, pswpOptions);
      gallery.init();
    }

    function openShellGalleryZoom() {
      if (!config.shellImages || config.shellImages.length === 0) return;

      var pswpElement = document.querySelectorAll('.pswp')[0];
      if (!pswpElement || typeof PhotoSwipe === 'undefined') return;

      /* Build items matching theme.Photoswipe getImageData exactly —
         msrc from DOM element, src/w/h from data attributes, el for getThumbBoundsFn */
      var activeMainImg = container.querySelector('[data-gallery-main-img]');
      var items = config.shellImages.map(function (img, i) {
        return {
          msrc: activeMainImg ? (activeMainImg.currentSrc || activeMainImg.src) : img.src,
          src: img.zoom || img.src,
          w: img.w || 1200,
          h: img.h || 1500,
          el: activeMainImg, /* All items point to the single visible img for getThumbBoundsFn */
          initialZoomLevel: 0.5
        };
      });

      var activeThumb = container.querySelector('.bundle-builder__gallery-thumb.is-active');
      var startIdx = activeThumb ? parseInt(activeThumb.dataset.galleryThumb, 10) : 0;
      if (startIdx >= items.length) startIdx = 0;

      /* Options match theme.Photoswipe initGallery exactly */
      var options = {
        allowPanToNext: false,
        captionEl: false,
        closeOnScroll: false,
        counterEl: false,
        history: false,
        index: startIdx, /* 0-based — theme passes index-1 because it gets 1-based from data-index */
        pinchToClose: false,
        preloaderEl: false,
        scaleMode: 'zoom',
        shareEl: false,
        tapToToggleControls: false,
        getThumbBoundsFn: function (index) {
          var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
          var thumbnail = items[index] && items[index].el;
          if (!thumbnail) return null;
          var rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        }
      };

      var gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);

      /* Dispatch afterChange event matching theme.Photoswipe behaviour */
      gallery.listen('afterChange', function () {
        container.dispatchEvent(new CustomEvent('photoswipe:afterChange', {
          detail: { index: gallery.getCurrentIndex() }
        }));
      });

      gallery.init();

      /* Match theme.Photoswipe preventiOS15Scrolling */
      if (/iPhone|iPad|iPod/i.test(window.navigator.userAgent)) {
        var initialScrollPos = window.scrollY;
        document.documentElement.style.setProperty('--window-inner-height', window.innerHeight + 'px');
        document.documentElement.classList.add('pswp-open-in-ios');
        function syncHeight() {
          document.documentElement.style.setProperty('--window-inner-height', window.innerHeight + 'px');
        }
        window.addEventListener('resize', syncHeight);
        gallery.listen('destroy', function () {
          document.documentElement.classList.remove('pswp-open-in-ios');
          window.scrollTo(0, initialScrollPos);
          window.removeEventListener('resize', syncHeight);
        });
      }
    }

    initZoom();

    /* ── Size guide URL link handler ── */
    function initSizeGuideLinks() {
      container.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-size-guide-trigger]');
        if (!trigger) return;
        e.preventDefault();
        openSizeGuideModal(trigger.getAttribute('data-size-guide-trigger'), trigger);
      });
    }
    initSizeGuideLinks();

    console.log('[BundleBuilder] Initialized v3', {
      sectionId: sectionId,
      bundleSize: config.bundleSize,
      variantCount: Object.keys(variants).length,
      colorCount: options.colors.length,
      hiddenCounts: { sizes: Object.keys(hiddenSizes).length, inseams: Object.keys(hiddenInseams).length, colors: Object.keys(hiddenColors).length }
    });
  }

  /* ============================
     BOOTSTRAP
     ============================ */
  function init() {
    var builders = document.querySelectorAll('.bundle-builder[data-section-id]');
    builders.forEach(initBundleBuilder);
  }

  var initialized = false;
  function safeInit() {
    if (initialized) return;
    initialized = true;
    init();
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(safeInit, 100);
  }
  document.addEventListener('page:loaded', safeInit);
  document.addEventListener('DOMContentLoaded', function () { setTimeout(safeInit, 200); });
  window.addEventListener('load', safeInit);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      var section = evt.target.querySelector('.bundle-builder[data-section-id]');
      if (section) initBundleBuilder(section);
    });
  }

  /* ============================================
     SIZE GUIDE URL MODAL
     Fetches a linked page and renders its main
     content inside the theme's <tool-tip> modal
     structure, inheriting all existing CSS.
     - role=dialog + aria-modal on the card
     - Focus moves to close button on open;
       returns to trigger button on close
     - Escape key and backdrop click both dismiss
     - Tab cycles through all focusable elements
     ============================================ */
  function openSizeGuideModal(url, triggerEl) {
    var existing = document.getElementById('bb-size-guide-modal');
    if (existing) existing.remove();

    var previousFocus = triggerEl || document.activeElement;

    /* Reuse theme's <tool-tip> element so all existing CSS applies */
    var toolTip = document.createElement('tool-tip');
    toolTip.setAttribute('data-tool-tip-open', 'true');
    toolTip.id = 'bb-size-guide-modal';

    /* Modal card */
    var inner = document.createElement('div');
    inner.className = 'tool-tip__inner';
    inner.setAttribute('role', 'dialog');
    inner.setAttribute('aria-modal', 'true');
    inner.setAttribute('aria-label', 'Size Guide');

    /* Close button — matches theme .tool-tip__close */
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tool-tip__close';
    closeBtn.setAttribute('aria-label', 'Close size guide');
    closeBtn.innerHTML = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 20 20"><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>';

    /* Scrollable content area */
    var content = document.createElement('div');
    content.className = 'tool-tip__content';
    content.innerHTML = '<p>Loading\u2026</p>';

    inner.appendChild(closeBtn);
    inner.appendChild(content);
    toolTip.appendChild(inner);
    document.body.appendChild(toolTip);

    closeBtn.focus();

    /* Fetch the linked page and extract its main content */
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var main = doc.querySelector('#MainContent, main, [role="main"], .main-content');
        content.innerHTML = main ? main.innerHTML : doc.body.innerHTML;
      })
      .catch(function () {
        content.innerHTML = '<p>Unable to load size guide. <a href="' + url + '">View full page \u2192</a></p>';
      });

    function dismiss() {
      if (toolTip.parentNode) toolTip.remove();
      document.removeEventListener('keydown', onKeyDown);
      try { if (previousFocus && previousFocus.focus) previousFocus.focus(); } catch (e) {}
    }

    function getFocusable() {
      return Array.from(inner.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
    }

    function onKeyDown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); dismiss(); return; }
      if (e.key === 'Tab' || e.keyCode === 9) {
        e.preventDefault();
        var focusable = getFocusable();
        if (!focusable.length) return;
        var current = focusable.indexOf(document.activeElement);
        var next = e.shiftKey
          ? (current <= 0 ? focusable.length - 1 : current - 1)
          : (current >= focusable.length - 1 ? 0 : current + 1);
        focusable[next].focus();
      }
    }

    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); dismiss(); });
    toolTip.addEventListener('click', function (e) { if (!inner.contains(e.target)) dismiss(); });
    document.addEventListener('keydown', onKeyDown);
  }

})();

/* ============================================
   BUNDLE CART GUARD
   When a customer removes one item from a
   bundle, all remaining items in that bundle
   are automatically removed. This keeps order
   confirmations accurate and prevents partial
   bundle states.

   Messaging is injected into AMP's own
   CartAnnouncementBlock so it appears inside
   the cart drawer on all screen sizes.
   ============================================ */
(function () {
  'use strict';

  /* Prevents re-entry while cleanup is in progress */
  var _guardActive = false;
  var _messageTimer = null;

  /* ── Group cart items by _bundle_id ── */
  function parseBundleGroups(items) {
    var groups = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var props = item.properties || {};
      var bundleId    = props['_bundle_id'];
      var bundleSize  = props['_bundle_size'];
      var bundleTitle = props['_bundle_title'] || props['Bundle'];
      var bundleUrl   = props['_bundle_url'] || '';
      if (!bundleId || !bundleSize) continue;
      if (!groups[bundleId]) {
        groups[bundleId] = {
          expectedSize: parseInt(bundleSize, 10),
          title: bundleTitle || 'Your bundle',
          url: bundleUrl,
          keys: []
        };
      }
      groups[bundleId].keys.push(item.key);
    }
    return groups;
  }

  /* ── Return bundles where item count doesn't match expected size ── */
  function findIncomplete(items) {
    var groups = parseBundleGroups(items);
    var incomplete = [];
    var ids = Object.keys(groups);
    for (var i = 0; i < ids.length; i++) {
      var g = groups[ids[i]];
      if (g.keys.length > 0 && g.keys.length !== g.expectedSize) {
        incomplete.push(g);
      }
    }
    return incomplete;
  }

  /* ── Remove items from cart by line key ── */
  function removeItemsByKey(keys) {
    var updates = {};
    for (var i = 0; i < keys.length; i++) {
      updates[keys[i]] = 0;
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  /* ── Show an accessible toast dialog for 6 seconds ──
   * - role=dialog + aria-modal so screen readers treat it as a modal
   * - aria-labelledby links the heading to the dialog
   * - Focus moves to close button on open; returns to previous element on close
   * - Escape key and click-outside both dismiss
   * - Close button meets 44x44px minimum touch target (WCAG 2.5.5)
   * - White on #1a1a1a passes WCAG AA contrast (15.3:1)
   * - Tab cycles between close button and shop link (if present)
   */
  function showToast(message, url) {
    var existing = document.getElementById('bb-guard-toast-wrap');
    if (existing) existing.remove();
    if (_messageTimer) { clearTimeout(_messageTimer); _messageTimer = null; }

    var previousFocus = document.activeElement;

    /* Backdrop */
    var wrap = document.createElement('div');
    wrap.id = 'bb-guard-toast-wrap';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483646',
      'background:transparent',
      'cursor:pointer'
    ].join(';');

    /* Dialog card */
    var dialog = document.createElement('div');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'bb-guard-toast-msg');
    dialog.style.cssText = [
      'position:fixed',
      'top:50%',
      'left:50%',
      'transform:translate(-50%,-50%)',
      'background:#1a1a1a',
      'color:#fff',
      'padding:18px 20px 18px 20px',
      'font-size:16px',
      'line-height:1.5',
      'text-align:center',
      'border-radius:8px',
      'max-width:325px',
      'width:calc(100% - 48px)',
      'box-shadow:0 8px 32px rgba(0,0,0,0.4)',
      'z-index:2147483647',
      'cursor:auto'
    ].join(';');

    /* Message */
    var msg = document.createElement('p');
    msg.id = 'bb-guard-toast-msg';
    msg.style.cssText = 'margin:0 0 ' + (url ? '12px' : '0') + ' 0;padding-right:25px;';
    msg.textContent = message;
    dialog.appendChild(msg);

    /* Shop again link — only rendered if a URL was stored */
    var shopLink = null;
    if (url) {
      shopLink = document.createElement('a');
      shopLink.href = url;
      shopLink.textContent = 'Shop bundle again \u2192';
      shopLink.style.cssText = [
        'display:inline-block',
        'color:#fff',
        'font-size:15px',
        'font-weight:600',
        'letter-spacing:0.5px',
        'text-decoration:underline',
        'text-underline-offset:2px'
      ].join(';');
      dialog.appendChild(shopLink);
    }

    /* Close button — large click area via padding, small visual X */
    var closeBtn = document.createElement('button');
    closeBtn.setAttribute('aria-label', 'Close notification');
    closeBtn.style.cssText = [
      'position:absolute',
      'top:0',
      'right:0',
      'background:none',
      'border:none',
      'color:rgba(255,255,255,0.7)',
      'font-size:15px',
      'line-height:1',
      'cursor:pointer',
      'padding:12px 15px',
      'min-height:44px',
      'min-width:44px',
      'display:flex',
      'align-items:center',
      'justify-content:center'
    ].join(';');
    closeBtn.innerHTML = '&#x2715;';
    dialog.style.position = 'fixed';
    dialog.appendChild(closeBtn);

    document.body.appendChild(wrap);
    document.body.appendChild(dialog);

    /* Move focus to close button */
    closeBtn.focus();

    /* Build focusable elements list for tab cycling */
    function getFocusable() {
      var list = [closeBtn];
      if (shopLink) list.push(shopLink);
      return list;
    }

    function dismiss() {
      if (wrap.parentNode) wrap.remove();
      if (dialog.parentNode) dialog.remove();
      if (_messageTimer) { clearTimeout(_messageTimer); _messageTimer = null; }
      document.removeEventListener('keydown', onKeyDown);
      try { if (previousFocus && previousFocus.focus) previousFocus.focus(); } catch (e) {}
    }

    function onKeyDown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); dismiss(); return; }
      if (e.key === 'Tab' || e.keyCode === 9) {
        e.preventDefault();
        var focusable = getFocusable();
        var current = focusable.indexOf(document.activeElement);
        var next = e.shiftKey
          ? (current <= 0 ? focusable.length - 1 : current - 1)
          : (current >= focusable.length - 1 ? 0 : current + 1);
        focusable[next].focus();
      }
    }

    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); dismiss(); });
    wrap.addEventListener('click', dismiss);
    document.addEventListener('keydown', onKeyDown);

    _messageTimer = setTimeout(dismiss, 8000);
  }

  /* ── Refresh AMP cart UI then show toast ── */
  function refreshAmpCart(message, url) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function () {
        try {
          if (window.AMP_API && typeof window.AMP_API.OPEN_CART === 'function') {
            window.AMP_API.OPEN_CART();
          }
        } catch (e) {}
        if (message) showToast(message, url || '');
      })
      .catch(function () {});
  }

  /* ── Remove remaining items and notify customer ── */
  function handleIncomplete(incomplete) {
    _guardActive = true;

    var allKeys  = [];
    var titles   = [];
    var firstUrl = '';
    for (var i = 0; i < incomplete.length; i++) {
      for (var k = 0; k < incomplete[i].keys.length; k++) {
        allKeys.push(incomplete[i].keys[k]);
      }
      titles.push(incomplete[i].title);
      if (!firstUrl && incomplete[i].url) firstUrl = incomplete[i].url;
    }

    removeItemsByKey(allKeys)
      .then(function () {
        var msg = titles.length === 1
          ? 'An item from your ' + titles[0] + ' was removed so your entire bundle was removed from the cart - bundles require all items for the discount.'
          : 'Items from your bundles were removed so your entire bundles were removed from the cart - bundles require all items for the discount.';
        _guardActive = false;
        refreshAmpCart(msg, firstUrl);
      })
      .catch(function (err) {
        _guardActive = false;
        console.error('[BundleCartGuard] Failed to remove incomplete bundle items:', err);
      });
  }

  /*
   * ── Hook into AMP_CART_UPDATED ──
   * AMP calls this after every cart update it initiates.
   * We check for incomplete bundles and trigger cleanup.
   * cartTarget is returned unmodified — message is now
   * injected via DOM after OPEN_CART renders.
   */
  window.AMP_CART_UPDATED = function (rawCart, cartTarget) {
    if (_guardActive || !rawCart || !Array.isArray(rawCart.items)) {
      return cartTarget || undefined;
    }

    var incomplete = findIncomplete(rawCart.items);
    if (incomplete.length > 0) {
      handleIncomplete(incomplete);
    }

    return cartTarget || undefined;
  };

  console.log('[BundleCartGuard] Installed');

})();